import { useMemo } from 'react';
import { PRODUCTS } from '../../data/products';
import { scaledIngredients } from '../../domain/planner/costs';
import { createCatalog, productOf } from '../../domain/price-book';
import type { Recipe } from '../../domain/recipe';
import { formatQuantity, productName, recipeName, recipeSteps } from '../i18n/format';
import { useMessages } from '../i18n/LanguageProvider';
import { FoodIcon, FoodPlate } from '../pictures/FoodPlate';
import { productPicture, recipePicture } from '../pictures/pictures';

/**
 * DE: Rezept mit Zeiten, Zutaten für die geplanten Portionen und Arbeitsschritten.
 * EN: Recipe with times, ingredients for the planned servings and steps.
 */
export function RecipeDetails({ recipe, servings }: { recipe: Recipe; servings: number }) {
  const { language, t } = useMessages();
  const catalog = useMemo(() => createCatalog(PRODUCTS), []);
  return (
    <article className="app-recipe">
      <div className="app-recipe-head">
        <FoodPlate picture={recipePicture(recipe.id)} size={72} dish />
        <div>
          <p className="app-lead">{recipeName(recipe, language)}</p>
          <p className="app-muted">
            {t.minutes(recipe.totalMinutes)}, {t.activeMinutes(recipe.activeMinutes)}
          </p>
        </div>
      </div>
      <h3>{t.ingredients}</h3>
      <ul className="app-rows">
        {scaledIngredients(recipe, servings).map((ingredient) => {
          const product = productOf(catalog, ingredient.productId);
          return (
            <li key={ingredient.productId} className="app-row">
              <span className="app-row-name">
                <FoodIcon picture={productPicture(product)} size={28} />
                {productName(product, language)}
              </span>
              <span className="app-row-value">
                {formatQuantity(ingredient.quantity, product.unit, language)}
              </span>
            </li>
          );
        })}
      </ul>
      <h3>{t.steps}</h3>
      <ol>
        {recipeSteps(recipe, language).map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>
    </article>
  );
}
